<template>
    <div>
        <scroll-top-button></scroll-top-button>
        <report-blocker v-if="isGeneratingReport"
            :currentCase="reportCurrentCase" 
            :reportCasesCount="selectedCases.length">
        </report-blocker>
        <service-blocker v-if="isFetchingServices"
            :currentService="currentService" 
            :serviceCount="serviceCount">
        </service-blocker>
        <top-bar 
            :serviceName="serviceName" 
            @logout="logout">
        </top-bar>
        <main class="main-content container">
                <service-selection
                    :adviserServices="adviserServices"
                    @selectService="selectService"/>
            <transition name='fade-up'>
                <employee-search v-if="serviceId"
                    @search="searchForEmployee"
                    @clearSearch="clearAllResults"
                    :serviceName="serviceName"
                    :isFetchingUsers="isFetchingUsers">
                </employee-search>
            </transition>
            <transition name='fade-up'>
                <employee-results
                    v-if="employeeResults"
                    :employeeResults="employeeResults"
                    :hasPerformedCaseSearch="hasPerformedCaseSearch"
                    :selectedEmployees="selectedEmployees"
                    :isFetchingUsers="isFetchingUsers"
                    :isFetchingCases="isFetchingCases"
                    @searchForCases="searchForCases"
                    @selectEmployee="selectEmployee"
                    @selectAllEmployees="selectAllEmployees">
                </employee-results>
            </transition>
            <transition name='fade-up'>
                <case-results
                    v-if="caseResults"
                    :caseResults="caseResults"
                    :reportEmployees="reportEmployees"
                    :selectedCases="selectedCases"
                    :isFetchingSummary="isFetchingSummary"
                    @selectCase="selectCase"
                    @selectAllEmployeeCases="selectAllEmployeeCases"
                    @getCaseSummary="getCaseSummary">
                </case-results>
            </transition>
            <missing-attachments-modal
                :isVisible="missingAttachmentsModalVisible"
                :missingAttachmentResponse ="missingAttachmentResponse"
                @showCaseSummary="showCaseSummary"
                @close="hideMissingAttachmentsModal">
            </missing-attachments-modal>
            <summary-modal
                :isVisible="summaryModalVisible"
                :summaryData="summaryData"
                @generateReport="generateReport"
                @close="hideSummaryModal">
            </summary-modal>
        </main>
    </div>
</template>

<script>
import TopBar from '../components/TopBar.vue';
import ServiceSelection from '../components/ServiceSelection.vue';
import EmployeeSearch from '../components/EmployeeSearch.vue';
import EmployeeResults from '../components/EmployeeResults.vue';
import CaseResults from '../components/CaseResults.vue';
import MissingAttachmentsModal from '../components/MissingAttachmentsModal.vue';
import SummaryModal from '../components/SummaryModal.vue';
import qs from 'qs';
import { getAdviserId, clearToken } from '../utils/tokenUtils';
import { setAxiosAuthHeader, setAxiosDatasetHeader } from '../utils/axiosUtils';
import ReportBlocker from '../components/ReportBlocker.vue';
import ServiceBlocker from '../components/ServiceBlocker.vue';
import ScrollToTopButton from '../components/ScrollToTopButton.vue';

export default {
    name: 'tool',
    components: {
        'top-bar': TopBar,
        'service-selection': ServiceSelection,
        'employee-search': EmployeeSearch,
        'employee-results': EmployeeResults,
        'case-results': CaseResults,
        'missing-attachments-modal': MissingAttachmentsModal,
        'summary-modal': SummaryModal,
        'report-blocker': ReportBlocker,
        'service-blocker': ServiceBlocker,
        'scroll-top-button': ScrollToTopButton,
    },
    data() {
        return {
            reportId: null,
            serviceId: 0,
            serviceName: '',
            adviserId: null,
            socketId: null,
            caseResults: null,
            employeeResults: null,
            adviserServices: [],
            selectedCases: [],
            selectedEmployees: [],
            reportEmployees: [],
            missingAttachmentResponse: null,
            summaryData: null,
            hasPerformedCaseSearch: false,
            reportCurrentCase: null,
            currentService: null,
            serviceCount: 0,
            summaryModalVisible: false,
            missingAttachmentsModalVisible: false,
            isGeneratingReport: false,
            isLoadingService: false,
            isFetchingServices: false,
            isFetchingUsers: false,
            isFetchingCases: false,
            isFetchingSummary: false,
        };
    },
    created() {
        this.getAccessToken();
        this.adviserId = getAdviserId();
        this.initialiseSignalR();
    },
    methods: {
        hideMissingAttachmentsModal() {
            this.missingAttachmentsModalVisible = false;
        },
        hideSummaryModal() {
            this.summaryModalVisible = false;
        },
        resetServiceProgress() {
            this.currentService = null;
        },
        resetReportProgress() {
            this.reportCurrentCase = null;
        },
        async initialiseSignalR() {
            this.socketId = await this.$progressHub.initialise();
            this.$progressHub.$on('receive-case-progress', caseNo => {
                this.reportCurrentCase = caseNo;
            });

            this.$progressHub.$on('receive-service-progress', serviceName => {
                this.currentService = serviceName;
            });

            this.$progressHub.$on('receive-service-count', serviceCount => {
                this.serviceCount = serviceCount;
            });

            this.fetchUserServices();
        },
        async fetchUserServices() {
            this.isFetchingServices = true;

            const { data } = await this.$axios
                .get(`/user/v1/services/${this.adviserId}/${this.socketId}`)
                .catch();

            if (data) {
                this.adviserServices = data;
            }

            this.isFetchingServices = false;
            this.resetServiceProgress();
        },
        selectService(serviceId) {
            const service = this.adviserServices.find(
                s => s.serviceId == serviceId
            );
            this.serviceName = !!service ? service.serviceName : '';
            this.serviceId = !!service ? service.serviceId : 0;
            const datasetName = !!service ? service.datasetName : null;
            setAxiosDatasetHeader(datasetName);
        },
        async searchForEmployee(searchRequest) {
            this.isFetchingUsers = true;
            const { firstName, lastName, staffId, dateOfBirth } = searchRequest;
            const { data } = await this.$axios
                .get(
                    `/employee/v1/search?firstName=${firstName ||
                        ''}&lastName=${lastName || ''}&staffId=${staffId ||
                        ''}&dateOfBirth=${dateOfBirth || ''}`
                )
                .catch();

            if (data) {
                this.employeeResults = data;
            }

            this.isFetchingUsers = false;
        },
        clearAllResults() {
            this.reportId = null;
            this.showGenerateReportButton = false;
            this.missingAttachmentsModalVisible = false;
            this.summaryModalVisible = false;
            this.summaryData = null;
            this.selectedEmployees = [];
            this.caseResults = null;
            this.employeeResults = null;
            this.selectedCases = [];
            this.hasPerformedCaseSearch = false;
            this.reportEmployees = [];
            this.missingAttachmentResponse = null;
        },
        async searchForCases() {
            this.isFetchingCases = true;
            this.hasPerformedCaseSearch = true;
            const employeeIds = this.selectedEmployees.map(
                employee => employee.id
            );
            const { data } = await this.$axios
                .post('/case/v1/employeeCases', employeeIds)
                .catch();

            if (data) {
                this.caseResults = data;
            }

            this.reportEmployees = [...this.selectedEmployees];
            this.isFetchingCases = false;
        },
        async getCaseSummary() {
            this.isFetchingSummary = true;
            const employeeCases = this.reportEmployees.map(e => {
                return {
                    employeeId: e.id,
                    cases: this.caseResults
                        .filter(c => c.employeeId === e.id)
                        .map(c => ({
                            caseId: c.caseId,
                            selected: this.selectedCases.includes(c.caseId),
                        })),
                };
            });

            const reportData = {
                serviceId: this.serviceId,
                adviserId: this.adviserId,
                employeeCases: employeeCases,
            };

            // save report request to server
            const { data: reportId } = await this.$axios
                .post('/report/v1/reportData', reportData)
                .catch();

            if (reportId) {
                this.reportId = reportId;
            }

            const { data } = await this.$axios
                .get(`/report/v1/missingAttachments/${this.reportId}`)
                .catch();

            if (data) {
                this.missingAttachmentResponse = data;
            }
            this.isFetchingSummary = false;

            if (this.missingAttachmentResponse.areThereMissingAttachments) {
                this.missingAttachmentsModalVisible = true;
            } else {
                this.showCaseSummary();
            }
        },
        async showCaseSummary() {
            this.isFetchingSummary = true;
            const { data: summaryData } = await this.$axios
                .get(`/report/v1/summary/${this.reportId}`)
                .catch();

            if (summaryData) {
                this.summaryData = summaryData;
            }

            this.missingAttachmentsModalVisible = false;
            this.isFetchingSummary = false;
            this.summaryModalVisible = true;
        },
        selectEmployee(event, employee) {
            const { checked } = event.target;
            if (checked) {
                this.selectedEmployees.push({
                    id: employee.id,
                    staffId: employee.staffId,
                    name: employee.firstName + ' ' + employee.lastName,
                });
            } else {
                this.selectedEmployees = this.selectedEmployees.filter(
                    e => e.id !== employee.id
                );
            }
        },
        selectAllEmployees(event) {
            const { checked } = event.target;
            if (checked) {
                this.selectedEmployees = this.employeeResults.map(employee => ({
                    id: employee.id,
                    staffId: employee.staffId,
                    name: employee.firstName + ' ' + employee.lastName,
                }));
            } else {
                this.selectedEmployees = [];
            }
        },
        selectCase(event, caseId) {
            const { checked } = event.target;
            if (checked) {
                this.selectedCases.push(caseId);
            } else {
                this.selectedCases = this.selectedCases.filter(
                    c => c != caseId
                );
            }
        },
        selectAllEmployeeCases(event, cases) {
            const { checked } = event.target;
            if (checked) {
                this.selectedCases.push(...cases);
            } else {
                this.selectedCases = this.selectedCases.filter(
                    c => !cases.includes(c)
                );
            }
        },
        async generateReport() {
            this.summaryModalVisible = false;
            this.isGeneratingReport = true;
            const employeeCases = this.reportEmployees.map(e => {
                return {
                    employeeId: e.id,
                    cases: this.caseResults
                        .filter(c => c.employeeId === e.id)
                        .map(c => ({
                            caseId: c.caseId,
                            selected: this.selectedCases.includes(c.caseId),
                        })),
                };
            });

            const fileResponse = await this.$axios
                .get(`/report/v1/generate/${this.reportId}/${this.socketId}`)
                .catch();

            this.isGeneratingReport = false;
            this.resetReportProgress();

            if (fileResponse) {
                this.downloadReport(fileResponse);
            }
        },
        async downloadReport(fileResponse) {
            const { data } = fileResponse;
            const blob = this.convertBase64ToBlob(
                data.fileContents,
                data.contentType
            );

            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.setAttribute('target', '_blank');
            link.setAttribute('type', 'hidden');
            link.setAttribute('download', data.fileDownloadName);
            link.href = url;
            document.body.appendChild(link);
            link.click();
        },
        convertBase64ToBlob(bytes, contentType) {
            let raw = atob(bytes);
            let uintArray = new Uint8Array(new ArrayBuffer(raw.length));
            for (var i = 0; i < raw.length; i++) {
                uintArray[i] = raw.charCodeAt(i);
            }
            return new Blob([uintArray], { type: contentType });
        },
        async getAccessToken() {
            const { data } = await this.$axios({
                method: 'post',
                url: process.env.VUE_APP_IDENTITY_TOKEN_URL,
                data: qs.stringify({
                    client_id: process.env.VUE_APP_CLIENT_ID,
                    client_secret: process.env.VUE_APP_CLIENT_SECRET,
                    grant_type: process.env.VUE_APP_GRANT_TYPE,
                    scope: process.env.VUE_APP_SCOPE,
                }),
            }).catch();

            if (data) {
                setAxiosAuthHeader(data.access_token);
            }
        },
        logout() {
            this.$progressHub.stop();
            setAxiosAuthHeader("");
            setAxiosDatasetHeader("");
            clearToken();
            this.$router.push({ name: 'Login' });
        },
    },
    watch: {
        serviceId() {
            this.clearAllResults();
        },
    },
};
</script>

<style lang="scss" scoped>
  .fade-up-enter-active,
  .fade-up-leave-active {
      transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  .fade-up-enter,
  .fade-up-leave-to {
      opacity: 0;
      transform: translateY(10px);
  }
</style>
